/**
 * serviceProductService — CRUD for /api/v1/services/products/.
 *
 * apiClient auto-transforms responses to camelCase and request bodies to
 * snake_case, so no manual key mapping is needed — only numeric coercion of
 * DRF Decimal fields (which arrive as strings at runtime).
 */

import { apiClient } from '@/shared/services/api';
import type {
  ServiceProduct,
  ServiceProductListItem,
  ServiceProductFormData,
  ServiceProductFilters,
  ServiceStats,
} from '../types';

function num(v: unknown): number {
  return v == null || v === '' ? 0 : Number(v);
}
function optNum(v: unknown): number | null {
  return v == null || v === '' ? null : Number(v);
}

function normalizeProduct(p: ServiceProduct): ServiceProduct {
  return {
    ...p,
    basePrice: num(p.basePrice),
    staffBasePrice: optNum(p.staffBasePrice),
    maxCapacity: optNum(p.maxCapacity),
    unitStock: optNum(p.unitStock),
  };
}
function normalizeListItem(p: ServiceProductListItem): ServiceProductListItem {
  return {
    ...p,
    basePrice: num(p.basePrice),
    maxCapacity: optNum(p.maxCapacity),
    unitStock: optNum(p.unitStock),
    todayRunsCount: optNum(p.todayRunsCount),
    todayBooked: optNum(p.todayBooked),
    unitsOut: optNum(p.unitsOut),
  };
}

export async function getServiceProducts(filters?: ServiceProductFilters): Promise<ServiceProductListItem[]> {
  const params: Record<string, string | number | boolean> = { page_size: 200 };
  if (filters?.search) params.search = filters.search;
  if (filters?.serviceType) params.service_type = filters.serviceType;
  if (filters?.bookingMode) params.booking_mode = filters.bookingMode;
  if (filters?.isActive !== undefined) params.is_active = filters.isActive;
  const res = await apiClient.get<{ results: ServiceProductListItem[]; count: number }>(
    '/api/v1/services/products/', { params },
  );
  return res.data.results.map(normalizeListItem);
}

export async function getServiceProduct(id: string): Promise<ServiceProduct> {
  const res = await apiClient.get<ServiceProduct>(`/api/v1/services/products/${id}/`);
  return normalizeProduct(res.data);
}

export async function createServiceProduct(data: ServiceProductFormData, locationId: string): Promise<ServiceProduct> {
  const res = await apiClient.post<ServiceProduct>('/api/v1/services/products/', { ...data, location: locationId });
  return normalizeProduct(res.data);
}

export async function updateServiceProduct(id: string, data: Partial<ServiceProductFormData>): Promise<ServiceProduct> {
  const res = await apiClient.patch<ServiceProduct>(`/api/v1/services/products/${id}/`, data);
  return normalizeProduct(res.data);
}

export async function deleteServiceProduct(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/services/products/${id}/`);
}

/** Operational KPIs for the Services page (catalog + runs strips). */
export async function getServiceStats(): Promise<ServiceStats> {
  const res = await apiClient.get<ServiceStats>('/api/v1/services/products/stats/');
  return { ...res.data, revenueWeek: num(res.data.revenueWeek) };
}
